import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Camera,
  Eye,
  EyeOff,
  Loader2,
  RefreshCcw,
  Save,
  Trash2,
  UploadCloud,
} from "lucide-react";
import { supabase } from "../lib/supabaseClient";

const initialForm = {
  title: "",
  category: "",
  description: "",
  display_order: 0,
  is_visible: true,
};

function getPublicImageUrl(path) {
  if (!path) return "";

  const { data } = supabase.storage.from("work-photos").getPublicUrl(path);
  return data.publicUrl;
}

export default function WorkGalleryManager({ adminFetch, functionUrl }) {
  const [gallery, setGallery] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading] = useState("idle");
  const [message, setMessage] = useState("");
  const fileInputRef = useRef(null);

  const sortedGallery = useMemo(() => {
    return [...gallery].sort((a, b) => {
      const orderA = Number(a.display_order || 0);
      const orderB = Number(b.display_order || 0);
      if (orderA !== orderB) return orderA - orderB;
      return new Date(b.created_at || 0) - new Date(a.created_at || 0);
    });
  }, [gallery]);

  async function loadGallery() {
    setLoading("loading");
    setMessage("");

    try {
      const data = await adminFetch(`${functionUrl}?action=gallery-list`);
      setGallery(data.gallery || []);
      setLoading("success");
    } catch (error) {
      console.error(error);
      setLoading("error");
      setMessage(error.message);
    }
  }

  useEffect(() => {
    loadGallery();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateForm(field, value) {
    setForm((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateGalleryItem(id, field, value) {
    setGallery((current) =>
      current.map((item) =>
        item.id === id
          ? {
              ...item,
              [field]: value,
            }
          : item
      )
    );
  }

  async function createGalleryItem(event) {
    event.preventDefault();

    if (!form.title.trim()) {
      setMessage("Title is required.");
      return;
    }

    if (!selectedFile) {
      setMessage("Choose a work photo first.");
      return;
    }

    setLoading("saving");
    setMessage("");

    try {
      const uploadData = new FormData();
      uploadData.append("title", form.title.trim());
      uploadData.append("category", form.category.trim());
      uploadData.append("description", form.description.trim());
      uploadData.append("display_order", String(form.display_order || 0));
      uploadData.append("is_visible", String(form.is_visible));
      uploadData.append("image", selectedFile);

      const data = await adminFetch(`${functionUrl}?action=gallery-create`, {
        method: "POST",
        body: uploadData,
      });

      setGallery((current) => [data.item, ...current]);
      setForm(initialForm);
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }

      setMessage("Work photo added.");
      setLoading("success");
    } catch (error) {
      console.error(error);
      setLoading("error");
      setMessage(error.message);
    }
  }

  async function saveGalleryItem(item) {
    setLoading(`saving-${item.id}`);
    setMessage("");

    try {
      const data = await adminFetch(`${functionUrl}?action=gallery-update`, {
        method: "POST",
        body: JSON.stringify({
          id: item.id,
          title: item.title,
          category: item.category,
          description: item.description,
          display_order: Number(item.display_order || 0),
          is_visible: Boolean(item.is_visible),
        }),
      });

      setGallery((current) =>
        current.map((galleryItem) =>
          galleryItem.id === item.id ? data.item : galleryItem
        )
      );

      setMessage("Work photo saved.");
      setLoading("success");
    } catch (error) {
      console.error(error);
      setLoading("error");
      setMessage(error.message);
    }
  }

  async function deleteGalleryItem(item) {
    const confirmed = window.confirm(
      `Delete "${item.title}" from the work gallery? This removes the image too.`
    );

    if (!confirmed) return;

    setLoading(`deleting-${item.id}`);
    setMessage("");

    try {
      await adminFetch(`${functionUrl}?action=gallery-delete`, {
        method: "POST",
        body: JSON.stringify({
          id: item.id,
        }),
      });

      setGallery((current) => current.filter((galleryItem) => galleryItem.id !== item.id));
      setMessage("Work photo deleted.");
      setLoading("success");
    } catch (error) {
      console.error(error);
      setLoading("error");
      setMessage(error.message);
    }
  }

  return (
    <section className="mt-6 rounded-[2rem] bg-white p-5 shadow-sm ring-1 ring-black/5">
      <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-end">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.24em] text-green-700">
            Work Gallery
          </p>
          <h2 className="mt-2 text-2xl font-black tracking-tight">
            Manage Public Work Photos
          </h2>
        </div>

        <button
          onClick={loadGallery}
          className="inline-flex items-center justify-center gap-2 rounded-xl border border-black/10 bg-white px-4 py-3 text-sm font-black transition hover:bg-lime-50"
        >
          <RefreshCcw className="h-4 w-4" />
          Refresh Gallery
        </button>
      </div>

      {message && (
        <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm font-bold text-slate-700 ring-1 ring-black/5">
          {message}
        </div>
      )}

      <form
        onSubmit={createGalleryItem}
        className="mt-5 grid gap-4 rounded-[1.5rem] bg-slate-50 p-5 ring-1 ring-black/5"
      >
        <div className="grid gap-4 lg:grid-cols-2">
          <label className="grid gap-2 text-sm font-black">
            Title
            <input
              value={form.title}
              onChange={(event) => updateForm("title", event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
              placeholder="Fresh mowing lines"
            />
          </label>

          <label className="grid gap-2 text-sm font-black">
            Category
            <input
              value={form.category}
              onChange={(event) => updateForm("category", event.target.value)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
              placeholder="Lawn Mowing"
            />
          </label>
        </div>

        <label className="grid gap-2 text-sm font-black">
          Description
          <textarea
            value={form.description}
            onChange={(event) => updateForm("description", event.target.value)}
            rows="3"
            className="resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
            placeholder="Clean cut with sharp mowing lines and trimmed edges."
          />
        </label>

        <div className="grid gap-4 lg:grid-cols-[1fr_auto_auto] lg:items-end">
          <label className="grid gap-2 text-sm font-black">
            Photo
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={(event) => setSelectedFile(event.target.files?.[0] || null)}
              className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
            />
          </label>

          <label className="grid gap-2 text-sm font-black">
            Order
            <input
              type="number"
              value={form.display_order}
              onChange={(event) => updateForm("display_order", Number(event.target.value))}
              className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500 lg:w-28"
            />
          </label>

          <label className="flex items-center gap-3 rounded-2xl bg-white px-4 py-3 text-sm font-black ring-1 ring-black/5">
            <input
              type="checkbox"
              checked={form.is_visible}
              onChange={(event) => updateForm("is_visible", event.target.checked)}
            />
            Visible
          </label>
        </div>

        <button
          type="submit"
          disabled={loading === "saving"}
          className="inline-flex items-center justify-center gap-2 rounded-2xl bg-green-700 px-5 py-3 font-black text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-70"
        >
          {loading === "saving" ? (
            <Loader2 className="h-5 w-5 animate-spin" />
          ) : (
            <UploadCloud className="h-5 w-5" />
          )}
          Add Work Photo
        </button>
      </form>

      <div className="mt-6 grid gap-4">
        {loading === "loading" && (
          <div className="flex items-center gap-3 rounded-2xl bg-slate-50 p-5 font-black ring-1 ring-black/5">
            <Loader2 className="h-5 w-5 animate-spin" />
            Loading work gallery...
          </div>
        )}

        {sortedGallery.length === 0 && loading !== "loading" && (
          <div className="rounded-2xl bg-slate-50 p-6 text-center ring-1 ring-black/5">
            <Camera className="mx-auto h-8 w-8 text-slate-400" />
            <h3 className="mt-3 text-xl font-black">No work photos uploaded yet.</h3>
            <p className="mt-2 text-sm font-semibold text-slate-500">
              Add photos here when the owner sends real work pictures.
            </p>
          </div>
        )}

        {sortedGallery.map((item) => {
          const imageUrl = getPublicImageUrl(item.image_path);
          const isSaving = loading === `saving-${item.id}`;
          const isDeleting = loading === `deleting-${item.id}`;

          return (
            <div
              key={item.id}
              className="grid gap-4 rounded-[1.5rem] bg-slate-50 p-4 ring-1 ring-black/5 lg:grid-cols-[220px_1fr]"
            >
              <div className="overflow-hidden rounded-2xl bg-slate-900">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={item.title}
                    className="h-48 w-full object-cover lg:h-full"
                  />
                ) : (
                  <div className="grid h-48 place-items-center text-white">
                    <Camera className="h-8 w-8" />
                  </div>
                )}
              </div>

              <div className="grid gap-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="grid gap-2 text-sm font-black">
                    Title
                    <input
                      value={item.title || ""}
                      onChange={(event) =>
                        updateGalleryItem(item.id, "title", event.target.value)
                      }
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
                    />
                  </label>

                  <label className="grid gap-2 text-sm font-black">
                    Category
                    <input
                      value={item.category || ""}
                      onChange={(event) =>
                        updateGalleryItem(item.id, "category", event.target.value)
                      }
                      className="rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
                    />
                  </label>
                </div>

                <label className="grid gap-2 text-sm font-black">
                  Description
                  <textarea
                    value={item.description || ""}
                    onChange={(event) =>
                      updateGalleryItem(item.id, "description", event.target.value)
                    }
                    rows="3"
                    className="resize-none rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500"
                  />
                </label>

                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <label className="grid gap-2 text-sm font-black">
                    Order
                    <input
                      type="number"
                      value={item.display_order || 0}
                      onChange={(event) =>
                        updateGalleryItem(item.id, "display_order", Number(event.target.value))
                      }
                      className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 font-semibold outline-none focus:border-green-500 sm:w-28"
                    />
                  </label>

                  <button
                    type="button"
                    onClick={() => updateGalleryItem(item.id, "is_visible", !item.is_visible)}
                    className={`inline-flex items-center justify-center gap-2 rounded-2xl px-5 py-3 font-black transition ${
                      item.is_visible
                        ? "bg-lime-100 text-green-800"
                        : "bg-slate-200 text-slate-600"
                    }`}
                  >
                    {item.is_visible ? <Eye className="h-5 w-5" /> : <EyeOff className="h-5 w-5" />}
                    {item.is_visible ? "Visible" : "Hidden"}
                  </button>

                  <button
                    type="button"
                    onClick={() => saveGalleryItem(item)}
                    disabled={isSaving}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-green-700 px-5 py-3 font-black text-white transition hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isSaving ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Save className="h-5 w-5" />
                    )}
                    Save
                  </button>

                  <button
                    type="button"
                    onClick={() => deleteGalleryItem(item)}
                    disabled={isDeleting}
                    className="inline-flex items-center justify-center gap-2 rounded-2xl bg-red-700 px-5 py-3 font-black text-white transition hover:bg-red-800 disabled:cursor-not-allowed disabled:opacity-70"
                  >
                    {isDeleting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Trash2 className="h-5 w-5" />
                    )}
                    Delete
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}